/**
 * プレビューサイズ算出
 * - PC / SP それぞれで「最小」「最大」を先に決め、その間をなだらかに補間する
 */

/**
 * PC サイズ用の軽い ease-in。
 * ほぼ線形に近いなだらかさで、500→1200 を均等に伸ばす。
 */
const PC_EASE_EXPONENT = 1.35;
/** SP のカラム追従用 */
const SP_EASE_EXPONENT = 1.5;

/** PC: 設定値（実サイト想定）の端点 */
export const PC_SETTING_PX_MIN = 500;
export const PC_SETTING_PX_MAX = 1_200;
export const PC_SETTING_PERCENT_MIN = 0.2;
export const PC_SETTING_PERCENT_MAX = 0.6;

/**
 * PC: モック画面に対する表示幅の端点（画面幅比率）
 * SPプレビュー（約90%）のサイズ感に合わせ、標準(500px/20%)でも画面をしっかり埋める
 */
export const PC_FILL_MIN = 0.84;
export const PC_FILL_MAX = 0.96;

/**
 * PC: モック画面に対する表示高さの端点（画面高さ比率）
 * 幅と同じ進行度で連動し、途中で高さ上限に頭打ちしない
 */
export const PC_HEIGHT_FILL_MIN = 0.72;
export const PC_HEIGHT_FILL_MAX = 0.92;

/** オーバーレイ等の小さい設定レンジ用 */
export const PC_SETTING_PX_FLOOR = 200;
export const PC_FILL_FLOOR = 0.62;

/**
 * SP: カラム幅の端点 → 端末スケールの端点
 */
export const SP_COLUMN_WIDTH_MIN = 260;
export const SP_COLUMN_WIDTH_MAX = 430;
export const SP_SCALE_MIN = 0.55;
export const SP_SCALE_MAX = 0.92;

export const SP_PREVIEW_DESIGN_WIDTH = 423;
export const SP_PREVIEW_DESIGN_HEIGHT = 877;

/**
 * PC iMac モックの画面枠（imac_bg.jpg に対する比率）
 * オーバーレイ／ポップアップで共通。CSS の top/width/height と同期
 */
export const PC_PREVIEW_SCREEN_WIDTH_RATIO = 0.925;
export const PC_PREVIEW_SCREEN_HEIGHT_RATIO = 0.625;

/**
 * 0..1 の ease-out（SP のカラム追従用）。
 */
export function easedProgress(from, to, value, exponent = SP_EASE_EXPONENT) {
  if (to === from) {
    return value >= to ? 1 : 0;
  }
  if (value <= from) {
    return 0;
  }
  if (value >= to) {
    return 1;
  }
  const t = (value - from) / (to - from);
  return 1 - Math.pow(1 - t, exponent);
}

/**
 * 0..1 の ease-in（PC サイズ用）。
 */
export function easedInProgress(from, to, value, exponent = PC_EASE_EXPONENT) {
  if (to === from) {
    return value >= to ? 1 : 0;
  }
  if (value <= from) {
    return 0;
  }
  if (value >= to) {
    return 1;
  }
  const t = (value - from) / (to - from);
  return Math.pow(t, exponent);
}

/**
 * 線形補間
 */
export function lerp(min, max, t) {
  return min + (max - min) * t;
}

/**
 * PC設定 → 0..1 の進行度（幅・高さ共通）
 */
export function resolvePcSizeProgress(maxWidth, isPercent) {
  if (isPercent) {
    const ratio = parseFloat(maxWidth) / 100;
    if (!Number.isFinite(ratio) || ratio <= 0) {
      return 0;
    }
    if (ratio < PC_SETTING_PERCENT_MIN) {
      return easedInProgress(0.15, PC_SETTING_PERCENT_MIN, ratio, 1) * 0.15;
    }
    return easedInProgress(PC_SETTING_PERCENT_MIN, PC_SETTING_PERCENT_MAX, ratio);
  }

  const px = typeof maxWidth === 'number' ? maxWidth : parseInt(maxWidth, 10);
  const pixelWidth = Number(px) || PC_SETTING_PX_MIN;

  if (pixelWidth <= PC_SETTING_PX_FLOOR) {
    return 0;
  }
  if (pixelWidth < PC_SETTING_PX_MIN) {
    return easedInProgress(PC_SETTING_PX_FLOOR, PC_SETTING_PX_MIN, pixelWidth, 1) * 0.15;
  }
  return easedInProgress(PC_SETTING_PX_MIN, PC_SETTING_PX_MAX, pixelWidth);
}

/**
 * PC設定（px）→ モック画面内の表示幅(px)
 */
export function mapPcSettingPxToPreviewWidth(settingPx, screenWidth) {
  const width = Math.max(screenWidth || 0, 1);
  const minPreview = width * PC_FILL_MIN;
  const maxPreview = width * PC_FILL_MAX;
  const floorPreview = width * PC_FILL_FLOOR;

  const px = Number(settingPx) || PC_SETTING_PX_MIN;

  if (px <= PC_SETTING_PX_FLOOR) {
    return Math.round(floorPreview);
  }

  if (px < PC_SETTING_PX_MIN) {
    const t = easedInProgress(PC_SETTING_PX_FLOOR, PC_SETTING_PX_MIN, px, 1);
    return Math.round(lerp(floorPreview, minPreview, t));
  }

  const t = easedInProgress(PC_SETTING_PX_MIN, PC_SETTING_PX_MAX, px);
  return Math.round(lerp(minPreview, maxPreview, t));
}

/**
 * PC設定（%）→ モック画面内の表示幅(px)
 */
export function mapPcSettingPercentToPreviewWidth(percentValue, screenWidth) {
  const width = Math.max(screenWidth || 0, 1);
  const minPreview = width * PC_FILL_MIN;
  const maxPreview = width * PC_FILL_MAX;
  const floorPreview = width * PC_FILL_FLOOR;

  const ratio = Number(percentValue);
  if (!Number.isFinite(ratio) || ratio <= 0) {
    return Math.round(minPreview);
  }

  if (ratio < PC_SETTING_PERCENT_MIN) {
    const t = easedInProgress(0.15, PC_SETTING_PERCENT_MIN, ratio, 1);
    return Math.round(lerp(floorPreview, minPreview, t));
  }

  const t = easedInProgress(PC_SETTING_PERCENT_MIN, PC_SETTING_PERCENT_MAX, ratio);
  return Math.round(lerp(minPreview, maxPreview, t));
}

/**
 * PC設定の進行度 → モック画面内の最大画像高さ(px)
 * 幅と同じ進行度で伸ばし、途中で高さ上限に張り付かないようにする。
 */
export function mapPcProgressToPreviewHeight(progress, screenHeight, reservedChrome = 0) {
  const height = Math.max(screenHeight || 0, 1);
  const usable = Math.max(height - reservedChrome, height * 0.4);
  const minPreview = usable * PC_HEIGHT_FILL_MIN;
  const maxPreview = usable * PC_HEIGHT_FILL_MAX;
  const t = Math.max(0, Math.min(1, Number(progress) || 0));

  return Math.round(lerp(minPreview, maxPreview, t));
}

/**
 * SP: カラム幅からスケールを算出
 */
export function mapSpColumnWidthToScale(availableWidth) {
  const width = Math.max(availableWidth || 0, 0);
  if (!width) {
    return SP_SCALE_MIN;
  }

  const widthProgress = easedProgress(
    SP_COLUMN_WIDTH_MIN,
    SP_COLUMN_WIDTH_MAX,
    width
  );
  let scale = lerp(SP_SCALE_MIN, SP_SCALE_MAX, widthProgress);
  scale = Math.min(scale, width / SP_PREVIEW_DESIGN_WIDTH);

  return Math.min(SP_SCALE_MAX, Math.max(SP_SCALE_MIN, scale));
}
